import React, { useMemo, useState } from 'react';
import { Card } from '../atoms/Card';
import { formatCurrency } from '../../utils/date';
import { prepareTreemapData, type TokenData, type TreemapDataPoint } from '../../utils/dashboard';
import { Treemap, ResponsiveContainer, Tooltip } from 'recharts';
import type { TreemapNode } from 'recharts/types/chart/Treemap';
import { useTheme } from '../../utils/hooks/useTheme';

// Fixed categorical order (blue, orange, aqua, yellow, magenta, green, violet) — never cycled.
// One slot per top token; anything beyond that folds into the neutral "Autres" gray below.
const CATEGORICAL_COLORS = ['#2a78d6', '#eb6834', '#1baf7a', '#eda100', '#e87ba4', '#008300', '#4a3aa7'];
const OTHERS_COLOR = '#898781';

interface TreemapCell {
  name: string;
  symbol: string;
  value: number;
  percentage: number;
  fill: string;
  clickable?: boolean;
  others?: TreemapDataPoint[];
  [key: string]: unknown;
}

// Distinct shades of the "Autres" gray, lightest to darkest, for the expanded breakdown
function shadeOthersColor(index: number, total: number): string {
  const t = total > 1 ? index / (total - 1) : 0;
  const lightness = 70 - t * 35; // 70% (light) down to 35% (dark)
  return `hsl(35, 6%, ${lightness}%)`;
}

function buildTreemapCells(tokens: TokenData[], othersExpanded: boolean): TreemapCell[] {
  const base = prepareTreemapData(tokens);
  const othersIndex = base.findIndex(item => item.name === 'Autres');

  if (!othersExpanded || othersIndex === -1) {
    return base.map((item, index) => ({
      ...item,
      fill: item.name === 'Autres' ? OTHERS_COLOR : CATEGORICAL_COLORS[index % CATEGORICAL_COLORS.length],
      clickable: item.name === 'Autres'
    }));
  }

  const topItems = base.slice(0, othersIndex).map((item, index) => ({
    ...item,
    fill: CATEGORICAL_COLORS[index % CATEGORICAL_COLORS.length]
  }));
  const others = base[othersIndex].others ?? [];
  const expandedItems = others.map((item, index) => ({
    ...item,
    fill: shadeOthersColor(index, others.length)
  }));

  return [...topItems, ...expandedItems];
}

// Pick black or white text for contrast against the cell's fill color (relative luminance)
function getTextColor(hex: string): string {
  const r = parseInt(hex.slice(1, 3), 16) / 255;
  const g = parseInt(hex.slice(3, 5), 16) / 255;
  const b = parseInt(hex.slice(5, 7), 16) / 255;
  const luminance = 0.2126 * r + 0.7152 * g + 0.0722 * b;
  return luminance > 0.55 ? '#0b0b0b' : '#ffffff';
}

const TreemapCellContent: React.FC<TreemapNode> = (props) => {
  const { x, y, width, height } = props;
  const cell = props as unknown as TreemapCell;
  const { resolvedTheme } = useTheme();

  if (!cell.fill || width <= 0 || height <= 0) return null;

  const cellStroke = resolvedTheme === 'dark' ? '#1f2937' : '#fcfcfb';
  const textColor = getTextColor(cell.fill);
  const showSymbol = width > 36 && height > 24;
  const showPercentage = width > 60 && height > 48;

  // Scale the symbol to fill the cell (bounded by both width and height), within a readable range
  const symbolFontSize = Math.max(14, Math.min(28, width / 3.5, height / 2.2));
  const percentageFontSize = Math.max(11, Math.min(15, symbolFontSize * 0.5));
  const percentageOffset = symbolFontSize * 0.75 + 4;

  return (
    <g style={cell.clickable ? { cursor: 'pointer' } : undefined}>
      <rect x={x} y={y} width={width} height={height} fill={cell.fill} stroke={cellStroke} strokeWidth={2} />
      {showSymbol && (
        <text
          x={x + width / 2}
          y={y + height / 2 - (showPercentage ? percentageOffset / 2 : 0)}
          textAnchor="middle"
          dominantBaseline="middle"
          fill={textColor}
          fontSize={symbolFontSize}
          fontWeight={700}
        >
          {cell.symbol}
        </text>
      )}
      {showPercentage && (
        <text
          x={x + width / 2}
          y={y + height / 2 + percentageOffset}
          textAnchor="middle"
          dominantBaseline="middle"
          fill={textColor}
          fontSize={percentageFontSize}
          opacity={0.85}
        >
          {cell.percentage.toFixed(1)}%
        </text>
      )}
    </g>
  );
};

interface TreemapTooltipProps {
  active?: boolean;
  payload?: Array<{ payload: TreemapCell }>;
}

const TreemapTooltip: React.FC<TreemapTooltipProps> = ({ active, payload }) => {
  const cell = payload?.[0]?.payload;
  if (!active || !cell) return null;

  return (
    <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-md px-3 py-2 text-sm">
      <p className="font-semibold text-gray-900 dark:text-gray-100">{cell.name}</p>
      <p className="text-gray-600 dark:text-gray-400">
        {formatCurrency(cell.value)} ({cell.percentage.toFixed(1)}%)
      </p>
      {cell.clickable && (
        <p className="text-gray-500 dark:text-gray-500 text-xs mt-1">Cliquer pour voir le détail</p>
      )}
    </div>
  );
};

interface PortfolioTreemapProps {
  tokens: TokenData[];
  isLoading?: boolean;
}

export const PortfolioTreemap: React.FC<PortfolioTreemapProps> = ({
  tokens,
  isLoading = false
}) => {
  const [othersExpanded, setOthersExpanded] = useState(false);

  const data = useMemo(() => buildTreemapCells(tokens, othersExpanded), [tokens, othersExpanded]);
  const othersCell = data.find(cell => cell.name === 'Autres');
  const hasOthersToExpand = !othersExpanded && !!othersCell;
  const othersList = hasOthersToExpand ? (othersCell?.others ?? []) : [];

  const handleCellClick = (node: unknown) => {
    const cell = node as TreemapCell | undefined;
    if (cell?.name === 'Autres') {
      setOthersExpanded(true);
    }
  };

  if (isLoading) {
    return (
      <Card className="p-6">
        <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100 mb-4">
          Répartition du Portfolio
        </h2>
        <div className="h-64 flex items-center justify-center text-gray-500 dark:text-gray-400">
          Chargement du graphique...
        </div>
      </Card>
    );
  }

  return (
    <Card className="p-6">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100">
          Répartition du Portfolio
        </h2>
        {othersExpanded && (
          <button
            type="button"
            onClick={() => setOthersExpanded(false)}
            className="text-sm text-blue-600 dark:text-blue-400 hover:underline"
          >
            ◀ Regrouper les petits tokens
          </button>
        )}
      </div>
      {hasOthersToExpand && (
        <p className="text-xs text-gray-500 dark:text-gray-400 mb-2">
          Cliquez sur la case &laquo;&nbsp;Autres&nbsp;&raquo; pour voir chaque token individuellement.
        </p>
      )}
      {data.length > 0 ? (
        <ResponsiveContainer width="100%" height={300}>
          <Treemap
            data={data}
            dataKey="value"
            nameKey="name"
            aspectRatio={4 / 3}
            stroke="#fcfcfb"
            content={(props: TreemapNode) => <TreemapCellContent {...props} />}
            onClick={handleCellClick}
          >
            <Tooltip content={<TreemapTooltip />} />
          </Treemap>
        </ResponsiveContainer>
      ) : (
        <div className="h-64 flex items-center justify-center text-gray-500 dark:text-gray-400">
          Pas assez de données pour afficher le graphique
        </div>
      )}
      {othersList.length > 0 && (
        <p className="mt-3 text-xs text-gray-500 dark:text-gray-400">
          {othersList.map(token => `${token.name} ${token.percentage.toFixed(1)}%`).join(' · ')}
        </p>
      )}
    </Card>
  );
};
